'use client'

import { CardRec } from '@/components/common/Card'
import { ErrorBlock } from '@/components/common/ErrorBlock'
import { usePage } from '@/components/providers/PageProvider'
import { type StatType } from '@/defs/stat'
import { useCardsRec } from '@/hooks/useCardsRec'
import { cn, isNonEmptyString } from '@/utils/commonUtil'
import { getRecName } from '@/utils/recUtil'

import { FeaturedCommonArticles } from '../FeaturedCommon/FeaturedCommonArticles/FeaturedCommonArticles'
import { FeaturedCommonFilters } from '../FeaturedCommon/FeaturedCommonFilters/FeaturedCommonFilters'
import { FeaturedCommonRoot } from '../FeaturedCommon/FeaturedCommonRoot/FeaturedCommonRoot'

import styles from './FeaturedRec.module.scss'
import { FeaturedRecInfo } from './FeaturedRecInfo/FeaturedRecInfo'
import { FeaturedRecJumpButtons } from './FeaturedRecJumpButtons/FeaturedRecJumpButtons'
import { FeaturedRecOutboundBar } from './FeaturedRecOutboundBar/FeaturedRecOutboundBar'
import { FeaturedRecPartners } from './FeaturedRecPartners/FeaturedRecPartners'
import { FeaturedRecPrecon } from './FeaturedRecPrecon/FeaturedRecPrecon'
import { FeaturedRecTopline } from './FeaturedRecTopline/FeaturedRecTopline'

type Props = {
  stats?: StatType[]
}

export const FeaturedRec = ({ stats }: Props) => {
  const { page } = usePage()
  const route = page?.route
  const rec = page?.featured?.rec
  const { card, cardPartner } = useCardsRec({ rec })
  const backgroundImages =
    !card?.hasContentWarning && !cardPartner?.hasContentWarning
      ? [card?.imageArt, cardPartner?.imageArt].filter(isNonEmptyString)
      : []

  if (!rec || !card) return <ErrorBlock />

  const cardNames = getRecName(rec)

  const title =
    route?.section === 'preconsFeatured'
      ? (card.precon ? `${card.precon} Precon` : page.metadata?.title) || 'Unknown'
      : cardNames
  const nameBack = card.layout === 'split' ? undefined : card.nameBack
  const subtitle = route?.section === 'preconsFeatured' ? cardNames : nameBack

  const isPrecon = route?.section === 'preconsFeatured'
  const isPartners = route?.section === 'partnersFeatured' || (!!rec.cardId && !!rec.partnerRef)

  let variant = 'standard'
  if (isPartners && !isPrecon) variant = 'wideCard'

  const hasFilters =
    route?.section === 'commandersFeatured' ||
    route?.section === 'cardsFeatured' ||
    route?.section === 'preconsFeatured'

  return (
    <FeaturedCommonRoot backgroundImages={backgroundImages}>
      <div className={cn(styles.container, { hasFilters, styles, variant })}>
        <FeaturedRecTopline />
        <FeaturedRecInfo stats={stats} subtitle={subtitle} title={title} />
        <div className={styles.card}>
          {isPrecon ? (
            <>
              <FeaturedRecPrecon />
              <FeaturedRecOutboundBar />
            </>
          ) : isPartners ? (
            <FeaturedRecPartners rec={rec} />
          ) : (
            <>
              <CardRec disableLink extend forceLoad rec={rec} statsHide />
              <FeaturedRecOutboundBar />
            </>
          )}
        </div>
        {hasFilters && <FeaturedCommonFilters />}
        {isPrecon && <FeaturedRecJumpButtons />}
        <FeaturedCommonArticles />
      </div>
    </FeaturedCommonRoot>
  )
}
